import {TabContext, TabList} from "@mui/lab";
import {Badge, Box, Checkbox, CircularProgress, FormControl, FormControlLabel, List, ListItem, ListItemText, MenuItem, Select, Skeleton, Tab, TextField, Tooltip, Typography} from "@mui/material";
import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import ChatPatient from "./ChatPatient";
import {Context} from "../../index";

const ChatList = observer(({
                           myChats,
                           openedChats,
                           closedChats,
                           closedChatsStats,
                           openedChatsStats,
                           loadChats,
                           search,
                           setSearch,
                           sortBy,
                           setSortBy,
                           filterByUnread,
                           setFilterByUnread,
                           selectedChat,
                           selectChat,
                           loading,
                           resetLoading,
                           hasMore,
                           categoryId,
                           onMarkAsRead,
                       }) => {
    const { chatStats } = useContext(Context);
    const [value, setValue] = useState("my");
    const endOfChats = useRef(null);
    const listRef = useRef(null);
    const prevScrollTopRef = useRef(null);
    const prevScrollHeightRef = useRef(null);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // Синхронизируем клиентские вычисления из myChats в стор (для реального времени)
    useEffect(() => {
        const unreadCount = myChats.filter(c => !c.isRead && !!c.lastMessageDateTime).length;
        const requestsCount = myChats.filter(c => c.hasActiveRequests).length;
        chatStats.setMyStats(categoryId || null, { unreadCount, requestsCount });
    }, [myChats, categoryId, chatStats]);

    const myChatsStats = chatStats.getMyStats(categoryId || null);

    useEffect(() => {
        loadChats(value, search[value], true);
    }, [value, categoryId]);

    useEffect(() => {
        if (!search[value]?.trim()) {
            loadChats(value, search[value], true);
        }
    }, [search]);

    useEffect(() => {
        loadChats(value, search[value], true);
    }, [sortBy, filterByUnread]);

    const onLoadMore = () => {
        if (!value) return;
        const canLoadMore = hasMore?.[value];
        if (loading || !canLoadMore) return;
        if (listRef.current) {
            prevScrollTopRef.current = listRef.current.scrollTop;
            prevScrollHeightRef.current = listRef.current.scrollHeight;
        }
        loadChats(value, search[value]);
    }

    const handleSearchChange = (e) => {
        setSearch(prev => ({
            ...prev,
            [value]: e.target.value
        }));
    };

    const handleSearchEnter = (e) => {
        if (e.key === 'Enter') {
            loadChats(value, search[value], true);
        }
    };

    const handleSortChange = (e) => {
        setSortBy(prev => ({
            ...prev,
            [value]: e.target.value
        }));
    };

    const handleFilterByUnreadChange = (e) => {
        setFilterByUnread(prev => ({
            ...prev,
            [value]: e.target.checked
        }));
    };

    const handleUnreadBadgeClick = (tab) => (e) => {
        e.stopPropagation();
        setValue(tab);
        setFilterByUnread(prev => ({ ...prev, [tab]: true }));
        setSortBy(prev => ({ ...prev, [tab]: 'recent' }));
    };

    const handleRequestsBadgeClick = (tab) => (e) => {
        e.stopPropagation();
        setValue(tab);
        setFilterByUnread(prev => ({ ...prev, [tab]: true }));
        setSortBy(prev => ({ ...prev, [tab]: 'requests' }));
    };

    useEffect(() => {
        if (!endOfChats.current) return;
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    onLoadMore();
                }
            },
            {threshold: 1, root: listRef.current}
        );
        observer.observe(endOfChats.current);
        return () => observer.disconnect();
    }, [value, loading, hasMore?.my, hasMore?.opened, hasMore?.closed]);

    useLayoutEffect(() => {
        if (!resetLoading && !loading && listRef.current && prevScrollTopRef.current != null) {
            listRef.current.scrollTop = prevScrollTopRef.current;
            prevScrollTopRef.current = null;
            prevScrollHeightRef.current = null;
        }
    }, [loading, resetLoading, value]);

    const renderSkeletons = (count = 6) => (
        [...Array(count)].map((_, idx) => (
            <ListItem key={idx}>
                <ListItemText
                    primary={<Skeleton variant="text" width="60%"/>}
                    secondary={<Skeleton variant="text" width="40%"/>}
                />
            </ListItem>
        ))
    );

    const renderChats = (chats) => {
        if (chats.length === 0) {
            return (
                <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "100%"}}>
                    <Typography variant="body1" sx={{opacity: 0.5, userSelect: "none"}}>Пусто</Typography>
                </Box>
            )
        }

        return chats.map(chat => (
            <ChatPatient
                key={chat.id}
                isRead={chat.isRead}
                patientName={chat.userName}
                patientPhone={chat.userPhone}
                lastMessageAt={chat.lastMessageDateTime}
                lastRequestDate={chat.lastRequestDate}
                message={chat.lastMessage}
                selected={selectedChat?.id === chat.id}
                hasActiveRequests={chat.hasActiveRequests}
                sortBy={sortBy[value]}
                adminName={chat.adminName}
                func={() => selectChat(chat.id)}
                onMarkAsRead={onMarkAsRead ? () => onMarkAsRead(chat.id) : undefined}
            />
        ));
    };

    const renderTabBadges = (stats, tab) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {stats?.unreadCount > 0 && (
                <Tooltip title="Чатов с непрочитанными сообщениями" arrow>
                    <Badge
                        badgeContent={stats.unreadCount}
                        color="primary"
                        onClick={handleUnreadBadgeClick(tab)}
                        sx={{
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': { transform: 'scale(1.15)' },
                            '& .MuiBadge-badge': {
                                position: 'static',
                                transform: 'none',
                                fontSize: '0.7rem',
                                height: '18px',
                                minWidth: '18px',
                                padding: '0 4px',
                                transition: 'all 0.2s ease-in-out'
                            }
                        }}
                    />
                </Tooltip>
            )}
            {stats?.requestsCount > 0 && (
                <Tooltip title="Чатов с необработанными заявками" arrow>
                    <Badge
                        badgeContent={stats.requestsCount}
                        color="error"
                        onClick={handleRequestsBadgeClick(tab)}
                        sx={{
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': { transform: 'scale(1.15)' },
                            '& .MuiBadge-badge': {
                                position: 'static',
                                transform: 'none',
                                fontSize: '0.7rem',
                                height: '18px',
                                minWidth: '18px',
                                padding: '0 4px',
                                transition: 'all 0.2s ease-in-out'
                            }
                        }}
                    />
                </Tooltip>
            )}
        </Box>
    );

    const makeTabLabel = (text, stats, tab) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{text}</span>
            {renderTabBadges(stats, tab)}
        </Box>
    );

    return (
        <Box
            sx={{
                width: 320,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}>
            <TabContext value={value}>
                <Box sx={{borderBottom: 1, borderColor: "divider", px: 1}}>
                    <TabList
                        onChange={handleChange}
                        aria-label="chat tabs"
                        sx={{
                            '& .MuiTab-root': { cursor: 'pointer' },
                            '& .Mui-selected': { cursor: 'default' }
                        }}
                    >
                        <Tab label={makeTabLabel('Мои', myChatsStats, 'my')} value="my"/>
                        <Tab label={makeTabLabel('В работе', openedChatsStats, 'opened')} value="opened"/>
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>Чаты</span>
                                    {renderTabBadges(closedChatsStats, 'closed')}
                                </Box>
                            }
                            value="closed"
                        />
                    </TabList>
                </Box>

                <TextField
                    placeholder={'Имя или номер телефона'}
                    fullWidth
                    size="small"
                    value={search[value]}
                    onKeyDown={handleSearchEnter}
                    onChange={handleSearchChange}
                    sx={{px: 1, my: 1}}
                />

                <FormControl fullWidth size="small" sx={{px: 1, mb: 1}}>
                    <Select
                        id="sort-select"
                        value={sortBy[value]}
                        onChange={handleSortChange}
                    >
                        <MenuItem value="recent">По дате сообщения</MenuItem>
                        <MenuItem value="requests">По дате заявки</MenuItem>
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filterByUnread[value]}
                            onChange={handleFilterByUnreadChange}
                            size="small"
                        />
                    }
                    label={sortBy[value] === 'requests' ? "Только необработанные" : "Только непрочитанные"}
                    sx={{px: 1, mb: 1}}
                />

                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                    }}
                    ref={listRef}
                >
                    <List>
                        {resetLoading
                            ? renderSkeletons()
                            : value === 'my'
                                ? renderChats(myChats)
                                : value === 'opened'
                                    ? renderChats(openedChats)
                                    : renderChats(closedChats)}
                        <div ref={endOfChats}/>
                        {loading && !resetLoading && hasMore?.[value] && (
                            <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                                <CircularProgress size={20}/>
                            </Box>
                        )}
                    </List>
                </Box>
            </TabContext>
        </Box>
    );
});


export default ChatList;
